if (!customElements.get('pants-size-select')) {
  class PantsSizeSelect extends HTMLElement {
    #isSyncing = false;

    get #isProductAvailable() {
      return this.dataset.productAvailable !== 'false';
    }

    connectedCallback() {
      this.hiddenSelect = this.querySelector('[data-pants-hidden-select]');
      this.waistSelect = this.querySelector('[data-pants-waist-select]');
      this.lengthSelect = this.querySelector('[data-pants-length-select]');
      this.sizeValueEl = this.querySelector('[data-size-option-value]');

      if (!this.hiddenSelect || !this.waistSelect || !this.lengthSelect) return;

      this.waistSelect.addEventListener('change', this.#onWaistChange);
      this.lengthSelect.addEventListener('change', this.#onLengthChange);
      document.addEventListener('pants-size:change', this.#onExternalSync);

      if (this.#isProductAvailable) {
        this.#markUnavailableWaists();
      } else {
        this.#applyProductAvailability();
      }

      this.syncFromCombined('', false);
    }

    disconnectedCallback() {
      this.waistSelect?.removeEventListener('change', this.#onWaistChange);
      this.lengthSelect?.removeEventListener('change', this.#onLengthChange);
      document.removeEventListener('pants-size:change', this.#onExternalSync);
    }

    isComplete() {
      return Boolean(this.waistSelect?.value && this.lengthSelect?.value);
    }

    syncFromCombined(combined, dispatchChange = false) {
      if (!this.waistSelect || !this.lengthSelect || !this.hiddenSelect) return;

      const parts = combined ? combined.split('/') : [];
      const waist = parts[0] || '';
      const length = parts[1] || '';

      this.waistSelect.value = waist;
      this.#populateLengthOptions(waist);
      this.#setLengthDisabled(!waist);
      this.lengthSelect.value = length;

      this.hiddenSelect.value = combined;
      if (this.sizeValueEl) {
        this.sizeValueEl.textContent = combined;
      }

      if (dispatchChange) {
        this.hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    #setLengthDisabled(isDisabled) {
      const shouldDisable = isDisabled || !this.#isProductAvailable;
      this.lengthSelect.disabled = shouldDisable;

      if (shouldDisable) {
        this.lengthSelect.setAttribute('disabled', 'disabled');
      } else {
        this.lengthSelect.removeAttribute('disabled');
      }
    }

    #applyProductAvailability() {
      if (this.#isProductAvailable) return;

      this.waistSelect.disabled = true;
      this.waistSelect.setAttribute('disabled', 'disabled');
      this.#setLengthDisabled(true);
    }

    #applyHiddenValue(combined, dispatchChange = false) {
      this.hiddenSelect.value = combined;

      if (this.sizeValueEl) {
        this.sizeValueEl.textContent = combined;
      }

      if (dispatchChange) {
        this.hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    #onWaistChange = (event) => {
      // Görünür select'in change'i variant-selects'e ulaşmasın (boş hidden ile
      // setUnavailable tetikler). Yalnızca hidden select'in change'i baloncuklanır
      event?.stopPropagation();

      if (this.#isSyncing || !this.#isProductAvailable) return;

      const waist = this.waistSelect.value;

      this.#populateLengthOptions(waist);
      this.lengthSelect.value = '';
      this.#setLengthDisabled(!waist);
      // Eksik seçim: change tetikleme. Buton "Sepete Ekle" kalır,
      // tıklanınca onSubmitHandler "Lütfen beden ve boy seçiniz" hatası gösterir
      this.#applyHiddenValue('', false);
      this.#broadcastSync();
    };

    #onLengthChange = (event) => {
      event?.stopPropagation();

      if (this.#isSyncing || !this.#isProductAvailable) return;

      const waist = this.waistSelect.value;
      const length = this.lengthSelect.value;
      const isComplete = Boolean(waist && length);
      const combined = isComplete ? `${waist}/${length}` : '';

      // Sadece beden+boy birlikte seçiliyken varyant değişimini tetikle
      this.syncFromCombined(combined, isComplete);
      // Varyant bilgisi (fiyat/stok) sunucudan dönene kadar butonu kilitle ki
      // bu aralıkta tıklayınca "stok yok" modalı açılmasın
      if (isComplete) this.#setAddButtonsLoading(true);
      this.#broadcastSync();
    };

    #setAddButtonsLoading(isLoading) {
      const section = this.dataset.section;
      const productForm = document.getElementById(`product-form-${section}`);
      const sectionRoot = productForm ? productForm.closest('.shopify-section') : null;

      // Sticky alan JS ile body'ye taşındığından ayrıca dahil edilir
      const buttons = new Set();
      if (sectionRoot) {
        sectionRoot.querySelectorAll('.single-add-to-cart-button').forEach((b) => buttons.add(b));
      }
      document
        .querySelectorAll(`product-add-to-cart-sticky[data-section="${section}"] .single-add-to-cart-button`)
        .forEach((b) => buttons.add(b));

      buttons.forEach((button) => {
        if (isLoading) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('loading');
        } else {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
        }
      });
    }

    #markUnavailableWaists() {
      const waistOptions = Array.from(this.waistSelect.options);

      for (const waistOption of waistOptions) {
        if (!waistOption.value) continue;

        const hasAvailableLength = Array.from(
          this.hiddenSelect.querySelectorAll(`option[data-waist="${CSS.escape(waistOption.value)}"]`)
        ).some((option) => option.dataset.available !== 'false');

        waistOption.disabled = !hasAvailableLength;
        this.#setStrikethrough(waistOption, !hasAvailableLength);
      }
    }

    // Native option'larda text-decoration çalışmadığından stoksuz değerler
    // Unicode birleşik üstü çizili karakter (U+0336) ile gösterilir
    #setStrikethrough(option, isUnavailable) {
      const label = option.dataset.label || option.textContent.trim();
      option.dataset.label = label;
      option.textContent = isUnavailable ? Array.from(label).map((char) => `${char}\u0336`).join('') : label;
    }

    #populateLengthOptions(waist) {
      const lengthSelect = this.lengthSelect;
      const placeholderText = lengthSelect.dataset.placeholder || '';

      lengthSelect.innerHTML = '';

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = placeholderText;
      lengthSelect.appendChild(placeholder);

      if (!waist) return;

      const hiddenOptions = Array.from(this.hiddenSelect.querySelectorAll('option[data-waist]'))
        .filter((option) => option.dataset.waist === waist);

      for (const hiddenOption of hiddenOptions) {
        const lengthOption = document.createElement('option');
        lengthOption.value = hiddenOption.dataset.length;
        lengthOption.textContent = hiddenOption.dataset.length;

        if (hiddenOption.dataset.available === 'false') {
          lengthOption.disabled = true;
          this.#setStrikethrough(lengthOption, true);
        }

        lengthSelect.appendChild(lengthOption);
      }
    }

    #broadcastSync() {
      document.dispatchEvent(new CustomEvent('pants-size:change', {
        detail: {
          sectionId: this.dataset.section,
          waist: this.waistSelect?.value || '',
          length: this.lengthSelect?.value || '',
          source: this
        }
      }));
    }

    #onExternalSync = (event) => {
      const detail = event.detail;
      if (!detail || detail.source === this) return;
      if (detail.sectionId !== this.dataset.section) return;

      this.#isSyncing = true;

      try {
        const waist = detail.waist || '';
        const length = detail.length || '';
        const combined = waist && length ? `${waist}/${length}` : '';

        this.waistSelect.value = waist;
        this.#populateLengthOptions(waist);
        this.#setLengthDisabled(!waist);
        this.lengthSelect.value = length;
        // Sadece görsel senkron: change tetikleme, aksi halde diğer
        // variant-selects.updateOther ile çakışıp seçimi sıfırlar
        this.#applyHiddenValue(combined, false);
      } finally {
        this.#isSyncing = false;
      }
    };
  }

  customElements.define('pants-size-select', PantsSizeSelect);
}

if (!customElements.get('variant-selects')) {

  /**
   *  @class
   *  @function VariantSelects
   */
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.sticky = this.dataset.sticky;
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.isDisabledFeature = this.dataset.isDisabled;
      this.other = Array.from(document.querySelectorAll('variant-selects')).filter((selector) => {
        return selector != this;
      });
      this.productWrapper = this.closest('.thb-product-detail');
      if (this.productWrapper) {
        this.productSlider = this.productWrapper.querySelector('.product-images') ? this.productWrapper.querySelector('.product-images') : this.productWrapper.querySelector('.product-quick-images');
        this.thumbnails = this.productWrapper.querySelector('.product-thumbnail-container');
        this.hideVariants = this.productSlider.dataset.hideVariants === 'true';
      }
    }

    connectedCallback() {
      this.updateOptions();
      this.updateMasterId();
      this.setDisabled();
      this.setImageSet();
      this.addEventListener('change', this.onVariantChange);
    }

    onVariantChange() {
      this.updateOptions();
      this.updateMasterId();
      this.toggleAddButton(true, '', false);
      this.updatePickupAvailability();
      this.removeErrorMessage();
      this.updateVariantText();
      this.setDisabled();

      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        this.updateMedia();
        if (this.updateUrl) {
          this.updateURL();
        }
        this.updateVariantInput();
        this.renderProductInfo();
        //this.updateShareUrl();
      }
      this.updateOther();
      dispatchCustomEvent('product:variant-change', {
        variant: this.currentVariant,
        sectionId: this.dataset.section
      });
    }

    updateOptions() {
      this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = [];
      this.option_keys = [];
      this.fieldsets.forEach((fieldset, i) => {
        if (fieldset.querySelector('select')) {
          this.options.push(fieldset.querySelector('select').value);
          this.option_keys.push(fieldset.querySelector('select').name);
        } else if (fieldset.querySelectorAll('input').length) {
          this.options.push(fieldset.querySelector('input:checked').value);
          this.option_keys.push(fieldset.querySelector('input').name);
        }
      });
      this.dataset.options = this.options;
    }
    updateVariantText() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      fieldsets.forEach((item, i) => {
        let label = item.querySelector('.form__label__value');
        if (label) {
          label.innerHTML = this.options[i];
        }
      });
    }
    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
    }

    updateOther() {
      if (this.dataset.updateUrl === 'false') {
        return;
      }
      if (this.other.length) {
        let fieldsets = this.other[0].querySelectorAll('fieldset'),
          fieldsets_array = Array.from(fieldsets);
        this.options.forEach((option, i) => {
          if (fieldsets_array[i].querySelector('select')) {
            const select = fieldsets_array[i].querySelector('select');
            select.value = option;
            const pantsSizeSelect = fieldsets_array[i].closest('pants-size-select');
            if (pantsSizeSelect && typeof pantsSizeSelect.syncFromCombined === 'function') {
              pantsSizeSelect.syncFromCombined(option || '', false);
            }
          } else if (fieldsets_array[i].querySelectorAll('input').length) {
            fieldsets_array[i].querySelector(`input[value="${option}"]`).checked = true;
          }
        });
        this.other[0].updateOptions();
        this.other[0].updateMasterId();
        this.other[0].updateVariantText();
        this.other[0].setDisabled();
      }
    }

    updateMedia() {
      if (!this.currentVariant) return;
      if (!this.currentVariant.featured_media) return;
      if (!this.productSlider) return;
      let mediaId = `#Slide-${this.dataset.section}-${this.currentVariant.featured_media.id}`;
      let activeMedia = this.productSlider.querySelector(mediaId);

      if (this.thumbnails) {
        this.setActiveMediaSlider(mediaId, `#Thumb-${this.dataset.section}-${this.currentVariant.featured_media.id}`, this.productSlider);
      } else {
        this.setActiveMedia(activeMedia);
      }

    }
    setActiveMedia(activeMedia) {

      this.productSlider.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });

      this.setImageSetMedia();

      activeMedia.classList.add('is-active');

      activeMedia.parentElement.prepend(activeMedia);

      if (!this.sticky) {
        window.setTimeout(() => {
          if (window.innerWidth > 1068) {
            let header_h = activeMedia.parentElement.offsetTop - parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')),
              scroll_obj = {
                left: 0,
                behavior: 'instant'
              };

            if (header_h > 0) {
              scroll_obj.top = header_h;
            }
            window.scrollTo(scroll_obj);
          }
          activeMedia.parentElement.scrollTo({
            left: 0,
            behavior: 'instant'
          });
        });
      }
    }
    setActiveMediaSlider(mediaId, thumbId, productSlider) {
      let flkty = Flickity.data(productSlider),
        activeMedia = productSlider.querySelector(mediaId);

      if (flkty && this.hideVariants) {
        if (productSlider.querySelector('.product-images__slide.is-initial-selected')) {
          productSlider.querySelector('.product-images__slide.is-initial-selected').classList.remove('is-initial-selected');
        }
        [].forEach.call(productSlider.querySelectorAll('.product-images__slide-item--variant'), function (el) {
          el.classList.remove('is-active');
        });
        if (this.thumbnails) {
          if (this.thumbnails.querySelector('.product-thumbnail.is-initial-selected')) {
            this.thumbnails.querySelector('.product-thumbnail.is-initial-selected').classList.remove('is-initial-selected');
          }
          [].forEach.call(this.thumbnails.querySelectorAll('.product-images__slide-item--variant'), function (el) {
            el.classList.remove('is-active');
          });
        }

        activeMedia.classList.add('is-active');
        activeMedia.classList.add('is-initial-selected');

        this.setImageSetMedia();

        if (this.thumbnails) {
          let activeThumb = this.thumbnails.querySelector(thumbId);

          activeThumb.classList.add('is-active');
          activeThumb.classList.add('is-initial-selected');
        }

        productSlider.reInit(this.imageSetIndex);
        productSlider.selectCell(mediaId);

      } else if (flkty) {
        productSlider.selectCell(mediaId);
      }

    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateShareUrl() {
      const shareButton = document.getElementById(`Share-${this.dataset.section}`);
      if (!shareButton) return;
      shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector('.pickup-availability-wrapper');

      if (!pickUpAvailability) return;

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.fetchAvailability(this.currentVariant.id);
      } else {
        pickUpAvailability.removeAttribute('available');
        pickUpAvailability.innerHTML = '';
      }
    }

    removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;

      const productForm = section.querySelector('product-form');
      if (productForm) productForm.handleErrorMessage();
    }

    getSectionsToRender() {
      return [`price-${this.dataset.section}`, `price-${this.dataset.section}--sticky`, `product-image-${this.dataset.section}--sticky`, `inventory-${this.dataset.section}`, `sku-${this.dataset.section}`, `quantity-${this.dataset.section}`];
    }

    renderProductInfo() {
      let sections = this.getSectionsToRender();

      fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          sections.forEach((id) => {
            const destination = document.getElementById(id);
            const source = html.getElementById(id);

            if (source && destination) destination.innerHTML = source.innerHTML;

            const price = document.getElementById(id);
            const price_fixed = document.getElementById(id + '--sticky');
            
            if (price) price.classList.remove('visibility-hidden');
            if (price_fixed) price_fixed.classList.remove('visibility-hidden');

          });
          const productBarcode = html.querySelector('[data-product-barcode]');
          const currentProductBarcode = document.querySelector('[data-product-barcode]');
          if (productBarcode && currentProductBarcode) {
            currentProductBarcode.textContent = productBarcode.textContent;
          }

          const kravatGenisligi = html.querySelector('[data-kravat-genisligi]');
          if (kravatGenisligi) {
            const currentKravatGenisligi = document.querySelector('[data-kravat-genisligi]');
            if (currentKravatGenisligi) {
              currentKravatGenisligi.textContent = kravatGenisligi.textContent;
            }
          }

          const nonErpAciklama4 = html.querySelector('[data-pdp-aciklama-4-hidden]');
          if (nonErpAciklama4) {
            const currentNonErpAciklama4 = document.querySelector('[data-pdp-aciklama-4]');
            if (currentNonErpAciklama4) {
              currentNonErpAciklama4.innerHTML = nonErpAciklama4.innerHTML;
            }
          }

          const sectionRoot = this.closest('.shopify-section') || this.closest('section');
          const sourceCampaign = html.querySelector('[data-campaign-text]');
          const currentCampaign = sectionRoot?.querySelector('[data-campaign-text]') ?? document.querySelector('[data-campaign-text]');

          if (sourceCampaign && currentCampaign) {
            currentCampaign.textContent = sourceCampaign.textContent;
          } else if (!sourceCampaign && currentCampaign) {
            currentCampaign.remove();
          } else if (sourceCampaign && !currentCampaign && sectionRoot) {
            const titleContainer = sectionRoot.querySelector('.product-title-container');
            const priceContainer = titleContainer?.querySelector('.product-price-container');
            if (titleContainer && priceContainer) {
              titleContainer.insertBefore(sourceCampaign.cloneNode(true), priceContainer.nextSibling);
            } else if (titleContainer) {
              titleContainer.appendChild(sourceCampaign.cloneNode(true));
            }
          }

          this.toggleAddButton(!this.currentVariant.available, window.theme.variantStrings.notifyMe);

        })
        .catch(() => {
          // Hata olsa bile butonu kilitli bırakma: varyant müsaitse tekrar aç
          if (this.currentVariant) {
            this.toggleAddButton(!this.currentVariant.available, window.theme.variantStrings.notifyMe);
          }
        });
    }

    toggleAddButton(disable = true, text = false, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) return;

      const productTemplate = productForm.closest('.product-form').getAttribute('template');
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');

      if (!submitButtons) return;

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');

        if (!submitButtonText) return;

        if (disable) {
          //submitButton.setAttribute('disabled', 'disabled');
          submitButton.setAttribute('type', 'button');
          submitButton.onclick = (event) => {
            openHollyNotificationStockOutModal(event.currentTarget);
          }
          if (text) submitButtonText.textContent = text;
        } else {
          submitButton.removeAttribute('disabled');
          submitButton.removeAttribute('aria-disabled');
          submitButton.setAttribute('type', 'submit');
          submitButton.classList.remove('loading');
          submitButton.onclick = null;

          if (productTemplate?.includes('pre-order')) {
            submitButtonText.textContent = window.theme.variantStrings.preOrder;
          } else {
            submitButtonText.textContent = window.theme.variantStrings.addToCart;
          }
        }
      });

      if (!modifyClass) return;
    }

    setUnavailable() {
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
      const price = document.getElementById(`price-${this.dataset.section}`);
      const price_fixed = document.getElementById(`price-${this.dataset.section}--sticky`);

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');
        if (!submitButton) return;
        submitButtonText.textContent = window.theme.variantStrings.unavailable;
        submitButton.classList.add('sold-out');
      });
      if (price) price.classList.add('visibility-hidden');
      if (price_fixed) price_fixed.classList.add('visibility-hidden');
    }

    setDisabled() {
      if (this.isDisabledFeature != 'true') {
        return;
      }
      const variant_data = this.getVariantData();


      if (variant_data) {
        let selected_options = false;
        if (this.currentVariant) {
          selected_options = this.currentVariant.options.map((value, index) => {
            return {
              value,
              index: `option${index + 1}`
            };
          });
        } else {
          let found_option = variant_data.find(option => {
            return option.option1 === this.options[0];
          });
          if (found_option) {
            selected_options = [
              {
                "value": this.options[0],
                "index": "option1"
              },
              {
                "value": found_option.option2,
                "index": "option2"
              }
            ];
          } else {
            return;
          }
        }

        const available_options = this.createAvailableOptionsTree(variant_data, selected_options);

        this.fieldsets.forEach((fieldset, i) => {
          const fieldset_options = Object.values(available_options)[i];

          if (fieldset_options) {
            if (fieldset.querySelector('select')) {
              fieldset_options.forEach((option, option_i) => {
                if (option.isUnavailable) {
                  fieldset.querySelector('option[value=' + JSON.stringify(option.value) + ']').disabled = true;
                } else {
                  fieldset.querySelector('option[value=' + JSON.stringify(option.value) + ']').disabled = false;
                }
              });
            } else if (fieldset.querySelectorAll('input[type="radio"]:not(:disabled)').length) {
              fieldset.querySelectorAll('input[type="radio"]:not(:disabled)').forEach((input) => {
                const optionEntry = fieldset_options.find((option) => option.value === input.value);
                if (optionEntry) {
                  input.classList.toggle('is-disabled', optionEntry.isUnavailable);
                } else {
                  input.classList.remove('is-disabled');
                }
              });
            }
          }
        });

      }
      return true;
    }

    getImageSetName(variant_name) {
      return variant_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
    }

    setImageSet() {
      if (!this.productSlider) return;

      let dataSetEl = this.productSlider.querySelector('[data-set-name]');
      if (dataSetEl) {
        this.imageSetName = dataSetEl.dataset.setName;
        this.imageSetIndex = this.querySelector('.product-form__input[data-handle="' + this.imageSetName + '"]').dataset.index;
        this.dataset.imageSetIndex = this.imageSetIndex;
        this.setImageSetMedia();
      }
    }

    setImageSetMedia() {
      if (!this.imageSetIndex) {
        return;
      }
      let setValue = this.getImageSetName(this.currentVariant[this.imageSetIndex]);
      let group = this.imageSetName + '_' + setValue;
      let selected_set_images = this.productSlider.querySelectorAll(`.product-images__slide[data-set-name="${this.imageSetName}"]`),
        selected_set_thumbs = this.productWrapper.querySelectorAll(`.product-thumbnail[data-set-name="${this.imageSetName}"]`);

      if (this.hideVariants) {
        if (this.thumbnails) {
          // Product images
          this.productWrapper.querySelectorAll('.product-images__slide').forEach(thumb => {
            if (thumb.dataset.group && thumb.dataset.group !== group) {
              thumb.classList.remove('is-active');
            }
          });
          selected_set_images.forEach(thumb => {
            thumb.classList.toggle('is-active', thumb.dataset.group === group);
          });

          // Product thumbnails
          this.productWrapper.querySelectorAll('.product-thumbnail').forEach(thumb => {
            if (thumb.dataset.group && thumb.dataset.group !== group) {
              thumb.classList.remove('is-active');
            }
          });
          selected_set_thumbs.forEach(thumb => {
            thumb.classList.toggle('is-active', thumb.dataset.group === group);
          });
        } else {
          // Product images
          this.productWrapper.querySelectorAll('.product-images__slide').forEach(thumb => {
            if (thumb.dataset.group && thumb.dataset.group !== group) {
              thumb.classList.remove('is-active');
            }
          });
          selected_set_images.forEach(thumb => {
            thumb.classList.toggle('is-active', thumb.dataset.group === group);
          });
        }

      } else {

        if (!this.thumbnails) {
          let set_images = Array.from(selected_set_images).filter(function (element) {
            return element.dataset.group === group;
          });
          set_images.forEach(thumb => {
            thumb.parentElement.prepend(thumb);
          });
        }

      }
      if (!this.thumbnails) {
        setTimeout(() => {
          this.productSlider.onPaginationResize();
        }, 100);
      }
    }

    createAvailableOptionsTree(variants, currentlySelectedValues) {
      // Reduce variant array into option availability tree
      return variants.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = { value: variant[index], isUnavailable: true };
            options[index].push(entry);
          }

          const currentOption1 = currentlySelectedValues.find(({ value, index }) => index === 'option1');
          const currentOption2 = currentlySelectedValues.find(({ value, index }) => index === 'option2');

          switch (index) {
            case 'option1':
              // Option1 inputs should always remain enabled based on all available variants
              entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
              break;
            case 'option2':
              // Option2 inputs should remain enabled based on available variants that match first option group
              if (currentOption1 && variant.option1 === currentOption1.value) {
                entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
              }
              break;
            case 'option3':
              // Option 3 inputs should remain enabled based on available variants that match first and second option group
              if (currentOption1 && variant.option1 === currentOption1.value && currentOption2 && variant.option2 === currentOption2.value) {
                entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
              }
          }
        });

        return options;
      }, { option1: [], option2: [], option3: [] });
    }

    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /**
   *  @class
   *  @function VariantRadios
   */
  class VariantRadios extends VariantSelects {
    constructor() {
      super();
    }

    updateOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = fieldsets.map((fieldset) => {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
      });
    }
  }

  customElements.define('variant-radios', VariantRadios);
}

if (!customElements.get('product-slider')) {
  /**
   * Keeps Shopify params like `v` (file version) while applying thumb transforms.
   */
  const buildPhotoSwipeThumbSrc = (fullSrc, width, height) => {
    if (!fullSrc) return fullSrc;

    try {
      const url = new URL(fullSrc, window.location.href);

      url.searchParams.set('width', String(width));
      url.searchParams.set('height', String(height));
      url.searchParams.set('crop', 'center');

      if (fullSrc.startsWith('//')) {
        return `//${url.host}${url.pathname}${url.search}`;
      }

      if (fullSrc.startsWith('/')) {
        return `${url.pathname}${url.search}`;
      }

      return url.toString();
    } catch {
      const base = fullSrc.split('?')[0];

      return `${base}?width=${width}&height=${height}&crop=center`;
    }
  };

  /**
   *  @class
   *  @function ProductSlider
   */
  class ProductSlider extends HTMLElement {
    constructor() {
      super();

    }
    connectedCallback() {
      this.pagination = this.parentElement.querySelector('.product-images-buttons');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');

      // Start Gallery
      let observer = new MutationObserver(() => {
        this.setupProductGallery();
      });

      observer.observe(this, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        characterData: false
      });

      this.setupProductGallery();

      // Start Pagination
      if (this.pagination) {
        this.setupPagination();
        this.resizeObserver = new ResizeObserver(entries => this.onPaginationResize());
        this.resizeObserver.observe(this);
        this.addEventListener('scroll', this.updatePagination.bind(this));
      }
    }
    setupProductGallery() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }

      this.setEventListeners();
    }
    buildItems(activeSlides) {
      let items = activeSlides.map((item) => {
        // Native (Shopify-hosted) video slide
        let nativeVideo = item.querySelector('.product-single__media-native-video video');
        if (nativeVideo) {
          let videoClone = nativeVideo.cloneNode(true);
          videoClone.removeAttribute('autoplay');
          videoClone.setAttribute('controls', '');
          videoClone.setAttribute('playsinline', '');
          return {
            html: `<div class="pswp__video-wrap pswp__video-wrap--native">${videoClone.outerHTML}</div>`,
            thumbSrc: nativeVideo.getAttribute('poster') || '',
            isVideo: true
          };
        }

        // External video slide (YouTube / Vimeo)
        let externalVideo = item.querySelector('.product-single__media-external-video iframe');
        if (externalVideo) {
          let previewImg = item.querySelector('.product-single__media-external-video--play img');
          return {
            html: `<div class="pswp__video-wrap pswp__video-wrap--external"><iframe src="${externalVideo.getAttribute('src')}" frameborder="0" allow="autoplay; encrypted-media; fullscreen" allowfullscreen></iframe></div>`,
            thumbSrc: previewImg ? previewImg.currentSrc || previewImg.src : '',
            isVideo: true
          };
        }

        let activelink = item.querySelector('.product-single__media-zoom');
        return {
          src: activelink.getAttribute('href'),
          msrc: activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h,
          title: activelink.getAttribute('title')
        };
      });
      return items;
    }
    setEventListeners() {
      const slider = this;
      let captionEl = this.dataset.captions,
        pswpElement = document.querySelectorAll('.pswp')[0],
        options = {
          maxSpreadZoom: 2,
          loop: false,
          allowPanToNext: false,
          closeOnScroll: false,
          showHideOpacity: false,
          arrowKeys: true,
          history: false,
          captionEl: captionEl,
          fullscreenEl: false,
          zoomEl: false,
          shareEl: false,
          counterEl: true,
          arrowEl: false,
          preloaderEl: true,
          clickToCloseNonZoomable: false,
          isClickableElement: function (el) {
            return el.tagName === 'A' || el.tagName === 'VIDEO' || el.tagName === 'IFRAME' || el.tagName === 'BUTTON' || (el.closest && el.closest('.pswp__video-wrap'));
          }
        };

      let createThumbnails = function(pswp, items) {
        const thumbnailsContainer = pswpElement.querySelector('.pswp__thumbnails-container');
        if (!thumbnailsContainer) return;
        
        thumbnailsContainer.innerHTML = '';
        
        items.forEach((item, index) => {
          const thumbEl = document.createElement('div');
          thumbEl.className = item.isVideo ? 'pswp__thumbnail pswp__thumbnail--video' : 'pswp__thumbnail';

          const isMobile = window.innerWidth <= 767;
          const baseSrc = item.isVideo ? item.thumbSrc : item.src;
          const thumbSrc = baseSrc
            ? (isMobile
              ? buildPhotoSwipeThumbSrc(baseSrc, 100, 150)
              : buildPhotoSwipeThumbSrc(baseSrc, 200, 300))
            : '';

          thumbEl.innerHTML = thumbSrc ? `<img src="${thumbSrc}" alt="">` : '';
          
          thumbEl.addEventListener('click', () => {
            pswp.goTo(index);
          });
          
          thumbnailsContainer.appendChild(thumbEl);
        });
        
          updateActiveThumbnail(pswp);
          
          enableThumbnailWheelScroll(thumbnailsContainer);
        };
        
        let enableThumbnailWheelScroll = function(container) {
          if (!container) return;
          
          container.addEventListener('wheel', (e) => {
            e.stopPropagation();
            
          }, { passive: true });
        };
        
        let updateActiveThumbnail = function(pswp) {
        const thumbnails = pswpElement.querySelectorAll('.pswp__thumbnail');
        const thumbnailsContainer = pswpElement.querySelector('.pswp__thumbnails-container');
        const currentIndex = pswp.getCurrentIndex();
        
        thumbnails.forEach((thumb, index) => {
          thumb.classList.toggle('pswp__thumbnail--active', index === currentIndex);
        });
        
        if (thumbnails[currentIndex] && thumbnailsContainer) {
          const activeThumbnail = thumbnails[currentIndex];
          const containerRect = thumbnailsContainer.getBoundingClientRect();
          const thumbnailRect = activeThumbnail.getBoundingClientRect();
          const isMobile = window.innerWidth <= 767;
          
          if (isMobile) {
            const isLeft = thumbnailRect.left < containerRect.left;
            const isRight = thumbnailRect.right > containerRect.right;
            
            if (isLeft || isRight) {
              const thumbnailWidth = activeThumbnail.offsetWidth + 8; // width + gap
              const containerWidth = thumbnailsContainer.clientWidth;
              const visibleThumbnails = Math.floor(containerWidth / thumbnailWidth);
              const centerOffset = Math.floor(visibleThumbnails / 2);
              
              const targetScrollLeft = Math.max(0, (currentIndex - centerOffset) * thumbnailWidth);
              
              thumbnailsContainer.scrollTo({
                left: targetScrollLeft,
                behavior: 'smooth'
              });
            }
          } else {
            const isAbove = thumbnailRect.top < containerRect.top;
            const isBelow = thumbnailRect.bottom > containerRect.bottom;
            
            if (isAbove || isBelow) {
              const thumbnailHeight = activeThumbnail.offsetHeight + 10; // height + gap
              const containerHeight = thumbnailsContainer.clientHeight;
              const visibleThumbnails = Math.floor(containerHeight / thumbnailHeight);
              const centerOffset = Math.floor(visibleThumbnails / 2);
              
              const targetScrollTop = Math.max(0, (currentIndex - centerOffset) * thumbnailHeight);
              
              thumbnailsContainer.scrollTo({
                top: targetScrollTop,
                behavior: 'smooth'
              });
            }
          }
        }
      };

      let openPswp = function (e, link, options, pswpElement) {
        const activeImages = Array.from(
          slider.querySelectorAll('.product-images__slide--image, .product-images__slide--video, .product-images__slide--external_video')
        ).filter((element) => element.clientWidth > 0);
        const items = slider.buildItems(activeImages);
        let parent = link.closest('.product-images__slide');
        let i = activeImages.indexOf(parent);
        options.index = parseInt(i, 10);
        options.getThumbBoundsFn = () => {
          const thumbnail = link.closest('.product-single__media'),
            pageYScroll = window.scrollY || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();
          return {
            x: rect.left,
            y: rect.top + pageYScroll,
            w: rect.width
          };
        };
        if (typeof PhotoSwipe !== 'undefined') {
          let pswp = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);

          pswp.listen('firstUpdate', () => {
            pswp.listen('parseVerticalMargin', function (item) {
              item.vGap = {
                top: 50,
                bottom: 50
              };
            });
            
            // Create thumbnails
            createThumbnails(pswp, items);
          });
          
          let stopLightboxVideos = () => {
            pswpElement.querySelectorAll('.pswp__video-wrap video').forEach((video) => video.pause());
            pswpElement.querySelectorAll('.pswp__video-wrap iframe').forEach((iframe) => {
              const wrap = iframe.closest('.pswp__item');
              if (!pswp.currItem || !wrap || !wrap.contains(pswp.currItem.container)) {
                iframe.setAttribute('src', iframe.getAttribute('src'));
              }
            });
          };

          pswp.listen('afterChange', () => {
            updateActiveThumbnail(pswp);
            stopLightboxVideos();
          });

          pswp.listen('close', () => {
            pswpElement.querySelectorAll('.pswp__video-wrap video').forEach((video) => video.pause());
          });
          
          // Add custom keyboard navigation for up/down arrows
          let keyboardHandler = (e) => {
            if (pswp.currItem) {
              switch(e.key) {
                case 'ArrowUp':
                  e.preventDefault();
                  pswp.prev();
                  break;
                case 'ArrowDown':
                  e.preventDefault();
                  pswp.next();
                  break;
              }
            }
          };
          
          pswp.listen('initialZoomIn', () => {
            document.addEventListener('keydown', keyboardHandler);
          });
          
          pswp.listen('close', () => {
            document.removeEventListener('keydown', keyboardHandler);
          });
          
          pswp.init();
        }
        e.preventDefault();
      };
      this.querySelectorAll('.product-single__media-zoom').forEach(function (link) {
        let thumbnail = link.closest('.product-single__media');
        let clone = link.cloneNode(true);
        thumbnail.append(clone);
        link.remove();
        clone.addEventListener('click', (e) => openPswp(e, clone, options, pswpElement));
      });

      this.video_containers.forEach((container) => {
        container.querySelector('button').addEventListener('click', function () {
          container.setAttribute('hidden', '');
        });
      });
    }
    setupPagination() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;

      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

      this.currentPageElement = this.pagination.querySelector('.slider-counter--current');
      this.pageTotalElement = this.pagination.querySelector('.slider-counter--total');

      this.prevButton = this.pagination.querySelector('button[name="previous"]');
      this.nextButton = this.pagination.querySelector('button[name="next"]');


      this.prevButton.addEventListener('click', this.onPaginationButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onPaginationButtonClick.bind(this));

      this.updatePagination();
    }
    onPaginationResize() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);

      if (this.sliderItemsToShow.length < 2) return;

      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      this.updatePagination();
    }
    onPaginationButtonClick(event) {
      event.preventDefault();
      this.slideScrollPosition = event.currentTarget.name === 'next' ? this.scrollLeft + (1 * this.sliderItemOffset) : this.scrollLeft - (1 * this.sliderItemOffset);
      this.scrollTo({
        left: this.slideScrollPosition
      });
    }
    updatePagination() {
      if (!this.nextButton) return;

      const previousPage = this.currentPage;
      this.currentPage = Math.round(this.scrollLeft / this.sliderItemOffset) + 1;

      if (this.currentPageElement) {
        this.currentPageElement.textContent = this.currentPage;
      }
      if (this.pageTotalElement) {
        this.pageTotalElement.textContent = this.sliderItemsToShow.length;
      }

      if (this.currentPage != previousPage) {
        this.dispatchEvent(new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1]
          }
        }));
      }

      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.scrollLeft === 0) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }
    isSlideVisible(element, offset = 0) {
      const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
      return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.scrollLeft;
    }
  }
  customElements.define('product-slider', ProductSlider);
}

/**
 *  @class
 *  @function ProductForm
 */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.sticky = this.dataset.sticky;
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      if (!this.form) return;
      this.form.querySelector('[name=id]').disabled = false;
      if (!this.sticky) {
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }
      this.cartNotification = document.querySelector('cart-notification');
      this.body = document.body;

      this.hideErrors = this.dataset.hideErrors === 'true';
    }
    onSubmitHandler(evt) {
      evt.preventDefault();

      const pantsSizeSelects = document.querySelectorAll(`pants-size-select[data-section="${this.dataset.section}"]`);
      if (pantsSizeSelects.length > 0) {
        const hasIncompleteSelection = Array.from(pantsSizeSelects).some((element) => !element.isComplete());

        if (hasIncompleteSelection) {
          const errorMessage = window.theme?.variantStrings?.selectSizeLength || 'Please select size and length.';
          this.handleErrorMessage(errorMessage);
          return;
        }
      }

      if (!this.form.reportValidity()) {
        return;
      }

      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');

      submitButtons.forEach((submitButton) => {
        if (submitButton.classList.contains('loading')) return;
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
      });

      this.handleErrorMessage();


      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };
      let formData = new FormData(this.form);

      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;
      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then(async (response) => {
          if (response.status) {
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message
            });
            if (response.status === 422) {
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true
              }));
            }
            this.handleErrorMessage(response.description);
            return;
          }

          const sections = this.getSectionsToRender().map((section) => section.section);
          const updatedSections = await checkAndUpdateCartDiscount(sections);

          if (updatedSections) {
            response.sections = updatedSections;
          }

          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          submitButtons.forEach((submitButton) => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
        });
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (section.id === 'Cart-Drawer') {
          document.getElementById('Cart-Drawer')?.notesToggle();
          document.getElementById('Cart-Drawer')?.removeProductEvent();
        }
        if (section.id === 'Cart' && typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      }));

      const product_drawer = document.getElementById('Product-Drawer');
      if (product_drawer && product_drawer.contains(this)) {
        document.body.classList.remove('open-cc--product');
        product_drawer.classList.remove('active');
        product_drawer.setAttribute('inert', '');
      }
      document.getElementById('Cart-Drawer')?.open();
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}

/**
 *  @class
 *  @function ProductAddToCartSticky
 */
if (!customElements.get('product-add-to-cart-sticky')) {
  class ProductAddToCartSticky extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
      this.setupObservers();
    }
    setupObservers() {
  const form = document.getElementById(`product-form-${this.dataset.section}`);
  if (!form) return;

  this.formPassed = false;

  const updateStickyState = () => {
    const formRect = form.getBoundingClientRect();
    const shouldBeVisible = formRect.bottom < 0;

    this.formPassed = shouldBeVisible;

    if (shouldBeVisible) {
      this.classList.add('sticky--visible');
    } else {
      this.classList.remove('sticky--visible');
    }
  };

  const scrollHandler = () => updateStickyState();
  window.addEventListener('scroll', scrollHandler, { passive: true });

  if (!window._stickyScrollPatched) {
    window._stickyScrollPatched = true;
    const originalScrollTo = window.scrollTo.bind(window);
    window.scrollTo = function(...args) {
      originalScrollTo(...args);
      for (const el of document.querySelectorAll('product-add-to-cart-sticky')) {
        if (typeof el._updateStickyState === 'function') {
          el._updateStickyState();
        }
      }
    };
  }

  this._updateStickyState = updateStickyState;
  
  this._scrollHandler = scrollHandler;
}

disconnectedCallback() {
  if (this._scrollHandler) {
    window.removeEventListener('scroll', this._scrollHandler);
  }
}

  }

  customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}

if (typeof addIdToRecentlyViewed !== "undefined") {
  addIdToRecentlyViewed();
}